import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { selectLanguage } from '../../store/languageSlice';
import { cvData } from '../../data/cvData';
import { translations } from '../../data/translations';
import { HiLocationMarker, HiCake, HiFlag } from 'react-icons/hi';
import { revealStagger, revealUp } from '../../utils/gsapReveal';
import './About.css';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const language = useSelector(selectLanguage);
  const sectionRef = useRef(null);
  const data = cvData[language];
  const t = translations[language];
  const { about, personal } = data;

  const stats = [
    { value: '7+', label: language === 'it' ? 'Anni di Esperienza' : 'Years Experience' },
    { value: '6+', label: language === 'it' ? 'Settori' : 'Industries' },
    { value: '10+', label: language === 'it' ? 'Progetti' : 'Projects' },
  ];

  const personalItems = [
    { icon: HiLocationMarker, label: t.about.personal.city, value: personal.city },
    { icon: HiCake, label: t.about.personal.birthDate, value: personal.birthDate },
    { icon: HiFlag, label: t.about.personal.citizenship, value: personal.citizenship },
  ];

  useLayoutEffect(() => {
    const scope = sectionRef.current;
    if (!scope) return;

    const ctx = gsap.context(() => {
      revealUp(scope.querySelector('.about__title'), { y: 48 });
      revealUp(scope.querySelector('.about__bio'), { y: 32, delay: 0.1 });

      const statsEl = scope.querySelector('.about__stats');
      if (statsEl) {
        revealStagger(statsEl, '.about__stat', { y: 36, stagger: 0.12 });
      }

      const personalGrid = scope.querySelector('.about-personal__grid');
      if (personalGrid) {
        revealStagger(personalGrid, '.about-personal__item', {
          y: 20,
          x: 24,
          stagger: 0.1,
          start: 'top 80%',
        });
      }

      revealUp(scope.querySelector('.about-personal__title'), {
        y: 24,
        start: 'top 82%',
      });
    }, scope);

    return () => ctx.revert();
  }, [language]);

  return (
    <section id="about" className="about" ref={sectionRef}>
      <div className="about__inner">
        <h2 className="about__title">{t.about.title}</h2>

        <p className="about__bio">{about.description}</p>

        <div className="about__bottom">
          <div className="about__stats">
            {stats.map((stat) => (
              <div key={stat.label} className="about__stat">
                <span className="about__stat-value">{stat.value}</span>
                <span className="about__stat-label">{stat.label}</span>
              </div>
            ))}
          </div>

          <div className="about-personal">
            <h3 className="about-personal__title">{t.about.personal.title}</h3>

            <dl className="about-personal__grid">
              {personalItems.map(({ icon: Icon, label, value }) => (
                <div key={label} className="about-personal__item">
                  <dt className="about-personal__label">
                    <Icon aria-hidden />
                    {label}
                  </dt>
                  <dd className="about-personal__value">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
